import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './ComputerVisionSolutionPage.scss';
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";

const ComputerVisionSolutionPage = () => {
    const [activeSection, setActiveSection] = useState('overview');
    const navigate = useNavigate();

    const handleContactClick = () => {
        navigate('/contact');
    };

    const sections = {
        overview: {
            title: "Overview",
            content: (
                <div className="section-content">
                    <p className="lead">
                        Computer vision is transforming industries—from manufacturing to healthcare. But building systems that actually work in production, across lighting changes, hardware constraints, and real-world variability, requires more than just training a model.
                    </p>
                    <div className="value-cards">
                        <div className="value-card">
                            <div className="icon">🎯</div>
                            <h3>Production Ready</h3>
                            <p>Built for real-world conditions and hardware constraints</p>
                        </div>
                        <div className="value-card">
                            <div className="icon">⚡</div>
                            <h3>Edge Optimized</h3>
                            <p>Efficient deployment on constrained devices</p>
                        </div>
                        <div className="value-card">
                            <div className="icon">🔧</div>
                            <h3>Full Stack</h3>
                            <p>Complete solution from hardware to deployment</p>
                        </div>
                    </div>
                </div>
            )
        },
        useCases: {
            title: "Use Cases",
            content: (
                <div className="section-content">
                    <div className="use-cases-grid">
                        <div className="use-case-card">
                            <h3>Manufacturing</h3>
                            <ul>
                                <li>Automated defect detection</li>
                                <li>Quality control automation</li>
                                <li>Assembly verification</li>
                            </ul>
                        </div>
                        <div className="use-case-card">
                            <h3>Security & Monitoring</h3>
                            <ul>
                                <li>Real-time object tracking</li>
                                <li>Anomaly detection</li>
                                <li>Behavior analysis</li>
                            </ul>
                        </div>
                        <div className="use-case-card">
                            <h3>Document Processing</h3>
                            <ul>
                                <li>Image analysis</li>
                                <li>Data extraction</li>
                                <li>Form processing</li>
                            </ul>
                        </div>
                        <div className="use-case-card">
                            <h3>Healthcare</h3>
                            <ul>
                                <li>Medical imaging</li>
                                <li>Diagnosis assistance</li>
                                <li>Anomaly detection</li>
                            </ul>
                        </div>
                    </div>
                </div>
            )
        },
        technical: {
            title: "Technical",
            content: (
                <div className="section-content">
                    <p className="lead">
                        We go beyond plug-and-play models to deliver full-stack, production-ready solutions:
                    </p>
                    <div className="tech-features-grid">
                        <div className="tech-feature">
                            <div className="feature-icon">🤖</div>
                            <div className="feature-content">
                                <h3>Proven Algorithms</h3>
                                <p>Well-tested architectures like YOLO series and other production-ready models for stability and speed</p>
                            </div>
                        </div>
                        <div className="tech-feature">
                            <div className="feature-icon">🔧</div>
                            <div className="feature-content">
                                <h3>Custom Development</h3>
                                <p>Tailored models for your specific use case and constraints</p>
                            </div>
                        </div>
                        <div className="tech-feature">
                            <div className="feature-icon">⚡</div>
                            <div className="feature-content">
                                <h3>Hybrid Architecture</h3>
                                <p>Combining traditional CV with deep learning for better performance</p>
                            </div>
                        </div>
                        <div className="tech-feature">
                            <div className="feature-icon">📱</div>
                            <div className="feature-content">
                                <h3>Edge Optimization</h3>
                                <p>Optimized for deployment on Jetson, Raspberry Pi, and mobile devices</p>
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        cases: {
            title: "Case Studies",
            content: (
                <div className="section-content">
                    <div className="case-studies-grid">
                        <div className="case-study">
                            <div className="case-study-content">
                                <h3>Vehicle Tracking and Counting</h3>
                                <p>Vehicle tracking and counting system combining YOLO with tracking algorithms.</p>
                            </div>
                        </div>
                        <div className="case-study">
                            <div className="case-study-content">
                                <h3>Steel Bar Counting</h3>
                                <p>Automated steel bar counting system with computer vision.</p>
                            </div>
                        </div>
                    </div>
                </div>
            )
        }
    };

    return (
        <div className="solution-page">
            <Helmet>
                <title>Computer Vision Solutions - Majority AI | AI Vision Systems</title>
                <meta name="description" content="Production-ready computer vision solutions by Majority AI. Object detection, image analysis, and video processing for manufacturing, healthcare, and more." />
                <meta name="keywords" content="computer vision, object detection, image recognition, video analytics, AI vision, MajorityAI CV" />
                <link rel="canonical" href="https://www.majorityai.com/computer-vision-solutions" />
            </Helmet>
            <Navbar/>
            <section className="slide-card-header">
                <div className='wrapper'>
                    <div className="outer-frame"></div>
                    <div className="inner-container">
                        <h1>Computer Vision<br/>Solutions</h1>
                    </div>
                </div>
            </section>

            <section className="content-section">
                <div className="wrapper2">
                    <div className="section-navigation">
                        {Object.entries(sections).map(([key, section]) => (
                            <button
                                key={key}
                                className={`nav-button ${activeSection === key ? 'active' : ''}`}
                                onClick={() => setActiveSection(key)}
                            >
                                {section.title}
                            </button>
                        ))}
                    </div>

                    <div className="section-container">
                        {sections[activeSection].content}
                    </div>

                    <div className="cta-section">
                        <h2>Ready to Transform Your Business?</h2>
                        <p>Let's discuss how our computer vision solutions can help you achieve your goals</p>
                        <button className="cta-button" onClick={handleContactClick}>Contact Us</button>
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    );
};

export default ComputerVisionSolutionPage;

